import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import Select from "react-select";

import { FORM_DATE_FORMAT } from "../../utils/Constants";
import { CopyAcademicAmi, GetAcademicCourseDetails } from "../../services/CourseService";
import { popupSuccessSimple } from "../../utils/Popups";
import PopupComponent from "../common/PopupComponent";

// AIE Standard Tooltip Component
const AIETooltip = ({ text, children }) => (
  <span
    className="aie-tooltip-container"
    style={{ position: "relative", display: "inline-flex" }}
    title={text}
  >
    {children}
  </span>
);

// AIE Standard Info Icon
const AIEInfoIcon = ({ text }) => (
  <AIETooltip text={text}>
    <span
      className="aie-info-icon"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "16px",
        height: "16px",
        backgroundColor: "#467fcf",
        color: "#ffffff",
        fontSize: "10px",
        fontWeight: "600",
        fontFamily: "Segoe UI",
        borderRadius: "10px",
        cursor: "pointer",
        marginLeft: "6px",
        lineHeight: 1,
        flexShrink: 0,
        transition: "background-color 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "#2766b9";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "#467fcf";
      }}
    >
      i
    </span>
  </AIETooltip>
);

export default function CopyAcademicAMIModal({ 
  courseId, 
  refresh, 
  onClose, 
  intakeOptions = [] // Receive intake options from parent (AllCourses)
}) {
  const mountedRef = useRef(true);

  const [loading, setLoading] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  
  // Format intake options for react-select - ensure both formats work
  const formattedIntakeOptions = intakeOptions.map(item => ({
    value: item.value, // Keep original value (may be with slash or hyphen)
    label: item.label || item.value.replace('/', '-') // Display with hyphen
  }));

  // Helper function to normalize intake format for validation
  const normalizeIntakeForValidation = (intake) => {
    if (!intake) return '';
    // Convert slash to hyphen for validation
    return intake.replace('/', '-');
  };

  // Helper function to normalize intake format for API
  const normalizeIntakeForAPI = (intake) => {
    if (!intake) return '';
    // The API expects forward slash, but our options already have it
    // If it somehow has hyphen, convert to slash
    return intake.replace('-', '/');
  };

  // Helper function to hide Bootstrap modal
  const hideModal = () => {
    const el = document.getElementById("copyAcademicAMIModal");
    if (!el) return;

    // Bootstrap 4 (jQuery) approach
    if (window.$ && typeof window.$ === "function" && window.$(el).modal) {
      window.$(el).modal("hide");
      return;
    }

    // Bootstrap 5 (vanilla JS) approach
    if (window.bootstrap?.Modal) {
      const instance = window.bootstrap.Modal.getInstance(el) || new window.bootstrap.Modal(el);
      instance.hide();
      return;
    }

    // Fallback: manually remove classes and backdrop
    el.classList.remove("show");
    el.style.display = "none";
    document.body.classList.remove("modal-open");
    const backdrop = document.querySelector(".modal-backdrop");
    if (backdrop) {
      backdrop.remove();
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      course_id: courseId || "",
      course_type: "scheduled",
      name: "",
      intake: "", // Will store the selected value
      start: null,
      end: null,
      link_content_assemblies: false,
      copy_assessments: false,
    },
    validationSchema: Yup.object({
      course_id: Yup.string().required("Course Id is required."),
      name: Yup.string().required("Course Name is required."),
      intake: Yup.string()
        .required("Intake is required.")
        .test('valid-intake-format', 'Intake must be in format YYYY-N or YYYY/N (e.g., 2026-1 or 2026/1)', function(value) {
          if (!value) return false;
          // Normalize to check both formats
          const normalized = value.replace('/', '-');
          return /^\d{4}-\d+$/.test(normalized);
        }),
      start: Yup.date().nullable().required("Start Date is required."),
      end: Yup.date().nullable().required("End Date is required."),
    }),
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);

      const startDateSeconds = values?.start ? Math.floor(values.start.getTime() / 1000) : null;
      const endDateSeconds = values?.end ? Math.floor(values.end.getTime() / 1000) : null;

      if (!startDateSeconds || !endDateSeconds) {
        formik.setErrors({
          start: !startDateSeconds ? "Start Date is required." : undefined,
          end: !endDateSeconds ? "End Date is required." : undefined,
        });
        setLoading(false);
        return;
      }

      if (startDateSeconds >= endDateSeconds) {
        formik.setErrors({
          start: "Start date must be before end date",
          end: "End date must be after start date",
        });
        setLoading(false);
        return;
      }

      // Use EXACT name typed by user
      const finalName = (values.name || "").trim();

      // Ensure intake is in correct format for API (forward slash)
      const apiIntake = normalizeIntakeForAPI(values.intake);

      const payload = {
        source_course_id: parseInt(values.course_id, 10),
        start_date: startDateSeconds,
        end_date: endDateSeconds,
        link_content_assemblies: values.link_content_assemblies,
        copy_assessments: values.copy_assessments,
        new_course_name: finalName,
        intake: apiIntake, // Send with forward slash
      };

      try {
        const res = await CopyAcademicAmi(payload);

        if (res?.status === 200) {
          // Close modal first
          handleClose();

          // Success message - display with hyphen for consistency
          const displayIntake = values.intake.replace('/', '-');

          popupSuccessSimple(
            `Academic AMI course copied successfully!\n` +
            `New code: ${res?.data?.new_course_code ?? ""}\n` +
            `Intake: ${displayIntake}\n` +
            `Assemblies linked: ${res?.data?.linked_content_assemblies || 0}\n` +
            `Assessments copied: ${res?.data?.copied_grade_mix_assessments || 0}\n` +
            `Assignments copied: ${res?.data?.copied_assignments || 0}`
          );

          resetForm();
          
          if (refresh) {
            refresh();
          }
        }
      } catch (err) {
        const errorData = err?.response?.data;
        let errorMessage = "Failed to copy course. Please try again.";

        if (Array.isArray(errorData) && errorData[0]?.message) {
          errorMessage = errorData[0].message;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        }

        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMessage,
        });
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
  });

  const fetchCourseDetails = async (id) => {
    if (!id) return;

    setFetchingDetails(true);

    try {
      const response = await GetAcademicCourseDetails(id);

      if (!mountedRef.current) return;

      const defaultName = response?.data?.course?.name
        ? `${response.data.course.name}`
        : "Copy of Academic AMI Course";

      formik.setFieldValue("course_id", id);
      formik.setFieldValue("course_type", "scheduled");
      formik.setFieldValue("name", defaultName);
      formik.setFieldValue("intake", ""); // Reset intake selection
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load course details. Please try again.",
      });
    } finally {
      if (mountedRef.current) {
        setFetchingDetails(false);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!courseId) return;

    fetchCourseDetails(courseId);
    formik.setFieldValue("start", null);
    formik.setFieldValue("end", null);
  }, [courseId]);

  const handleClose = () => {
    hideModal();
    formik.resetForm();
    setLoading(false);
    setFetchingDetails(false);
    
    if (onClose) {
      onClose();
    }
  };

  // Handle dropdown change
  const handleIntakeChange = (selectedOption) => {
    formik.setFieldValue("intake", selectedOption?.value || "");
    formik.setFieldTouched("intake", true);
  };

  return (
    <PopupComponent
      id="copyAcademicAMIModal"
      type="form"
      closeFunction={() => handleClose()}
      header={{
        iconName: "fa-copy",
        heading: "Copy Course",
      }}
      inputs={[
        <>
          {fetchingDetails ? (
            <div className="col-lg-12 text-center py-3">
              <i className="fas fa-spinner fa-spin fa-2x" style={{ color: "#467fcf" }}></i>
              <p className="mt-2" style={{ color: "#6c757d", fontFamily: "Segoe UI" }}>
                Loading course details...
              </p>
            </div>
          ) : (
            <>
              {/* Course Name Field */}
              <div className="col-lg-12">
                <div className="form-group-blk mb-3">
                  <label
                    htmlFor="courseName"
                    className="form-label fw-semibold"
                    style={{
                      fontFamily: "Segoe UI",
                      color: "#1d1d1d",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Course Name <span style={{ color: "#c1232c" }}>*</span>
                  </label>
                  <input
                    id="courseName"
                    type="text"
                    className={`form-control ${
                      formik.errors.name && formik.touched.name ? "is-invalid" : ""
                    }`}
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter Course Name"
                    disabled={fetchingDetails}
                    style={{
                      fontFamily: "Segoe UI",
                      fontSize: "14px",
                      borderRadius: "10px",
                      borderColor: "#dee2e6",
                    }}
                  />
                  {formik.errors.name && formik.touched.name && (
                    <div className="invalid-feedback d-block" style={{ color: "#c1232c" }}>
                      {formik.errors.name}
                    </div>
                  )}
                </div>
              </div>

              {/* Intake Dropdown Field */}
              <div className="col-lg-12">
                <div className="form-group-blk mb-3">
                  <label
                    htmlFor="intake"
                    className="form-label fw-semibold"
                    style={{
                      fontFamily: "Segoe UI",
                      color: "#1d1d1d",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Intake <span style={{ color: "#c1232c" }}>*</span>
                  </label>
                  <Select
                    id="intake"
                    name="intake"
                    options={formattedIntakeOptions}
                    value={formattedIntakeOptions.find(option => option.value === formik.values.intake)}
                    onChange={handleIntakeChange}
                    onBlur={() => formik.setFieldTouched("intake", true)}
                    placeholder="Select Intake"
                    isDisabled={fetchingDetails}
                    className={formik.errors.intake && formik.touched.intake ? "is-invalid" : ""}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderRadius: "10px",
                        borderColor: formik.errors.intake && formik.touched.intake ? "#c1232c" : "#dee2e6",
                        minHeight: "38px",
                        fontFamily: "Segoe UI",
                        fontSize: "14px",
                      }),
                      menu: (provided) => ({
                        ...provided,
                        fontFamily: "Segoe UI",
                        fontSize: "14px",
                      }),
                      option: (provided) => ({
                        ...provided,
                        fontFamily: "Segoe UI",
                        fontSize: "14px",
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        fontFamily: "Segoe UI",
                        fontSize: "14px",
                        color: "#6c757d",
                      }),
                    }}
                  />
                  {formik.errors.intake && formik.touched.intake && (
                    <div className="invalid-feedback d-block" style={{ color: "#c1232c" }}>
                      {formik.errors.intake}
                    </div>
                  )}
                  {formattedIntakeOptions.length === 0 && (
                    <small className="text-muted d-block mt-1">
                      No intake options available
                    </small>
                  )}
                </div>
              </div>

              {/* Date Fields Row */}
              <div className="col-lg-12">
                <div className="row">
                  {/* Start Date Field */}
                  <div className="col-lg-6 col-md-6">
                    <div className="form-group-blk mb-3">
                      <label
                        htmlFor="startDate"
                        className="form-label fw-semibold"
                        style={{
                          fontFamily: "Segoe UI",
                          color: "#1d1d1d",
                          fontSize: "14px",
                          marginBottom: "6px",
                        }}
                      >
                        Start Date <span style={{ color: "#c1232c" }}>*</span>
                      </label>
                      <DatePicker
                        id="startDate"
                        selected={formik.values.start}
                        dateFormat={FORM_DATE_FORMAT}
                        showYearDropdown
                        scrollableYearDropdown
                        dateFormatCalendar="MMMM"
                        className={`form-control cursor-pointer ${
                          formik.errors.start && formik.touched.start ? "is-invalid" : ""
                        }`}
                        placeholderText="Select Start Date"
                        onChange={(date) => {
                          formik.setFieldTouched("start", true);
                          formik.setFieldValue("start", date);
                        }}
                        onBlur={() => formik.setFieldTouched("start", true)}
                        onChangeRaw={(e) => e.preventDefault()}
                        disabled={fetchingDetails}
                        style={{
                          fontFamily: "Segoe UI",
                          fontSize: "14px",
                          borderRadius: "10px",
                          borderColor: "#dee2e6",
                          width: "100%",
                        }}
                      />
                      {formik.errors.start && formik.touched.start && (
                        <div className="invalid-feedback d-block" style={{ color: "#c1232c" }}>
                          {formik.errors.start}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* End Date Field */}
                  <div className="col-lg-6 col-md-6">
                    <div className="form-group-blk mb-3">
                      <label
                        htmlFor="endDate"
                        className="form-label fw-semibold"
                        style={{
                          fontFamily: "Segoe UI",
                          color: "#1d1d1d",
                          fontSize: "14px",
                          marginBottom: "6px",
                        }}
                      >
                        End Date <span style={{ color: "#c1232c" }}>*</span>
                      </label>
                      <DatePicker
                        id="endDate"
                        selected={formik.values.end}
                        dateFormat={FORM_DATE_FORMAT}
                        showYearDropdown
                        scrollableYearDropdown
                        dateFormatCalendar="MMMM"
                        className={`form-control cursor-pointer ${
                          formik.errors.end && formik.touched.end ? "is-invalid" : ""
                        }`}
                        placeholderText="Select End Date"
                        onChange={(date) => {
                          formik.setFieldTouched("end", true);
                          formik.setFieldValue("end", date);
                        }}
                        onBlur={() => formik.setFieldTouched("end", true)}
                        onChangeRaw={(e) => e.preventDefault()}
                        minDate={formik.values.start}
                        disabled={fetchingDetails}
                        style={{
                          fontFamily: "Segoe UI",
                          fontSize: "14px",
                          borderRadius: "10px",
                          borderColor: "#dee2e6",
                          width: "100%",
                        }}
                      />
                      {formik.errors.end && formik.touched.end && (
                        <div className="invalid-feedback d-block" style={{ color: "#c1232c" }}>
                          {formik.errors.end}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="col-lg-12">
                <hr className="my-3" style={{ borderColor: "#dee2e6" }} />
              </div>

              {/* Checkboxes Row */}
              <div className="col-lg-12">
                <div className="row">
                  {/* Link Content Assemblies */}
                  <div className="col-lg-6 col-md-6">
                    <div className="form-group-blk mb-0">
                      <div className="d-flex align-items-center">
                        <input
                          type="checkbox"
                          id="link_content_assemblies"
                          name="link_content_assemblies"
                          checked={formik.values.link_content_assemblies}
                          onChange={(e) => {
                            formik.setFieldValue("link_content_assemblies", e.target.checked);
                          }}
                          onBlur={formik.handleBlur}
                          disabled={fetchingDetails}
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                            marginTop: 0,
                            marginLeft: 0,
                            marginRight: "10px",
                            accentColor: "#467fcf",
                            flexShrink: 0,
                          }}
                        />
                        <label
                          className="fw-semibold d-flex align-items-center mb-0"
                          htmlFor="link_content_assemblies"
                          style={{
                            cursor: "pointer",
                            lineHeight: 1.4,
                            fontFamily: "Segoe UI",
                            fontSize: "14px",
                            color: "#1d1d1d",
                          }}
                        >
                          Link Content Assemblies
                          <AIEInfoIcon text="Links existing assemblies to the new course (does not duplicate)." />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Copy Assessments */}
                  <div className="col-lg-6 col-md-6">
                    <div className="form-group-blk mb-0">
                      <div className="d-flex align-items-center">
                        <input
                          type="checkbox"
                          id="copy_assessments"
                          name="copy_assessments"
                          checked={formik.values.copy_assessments}
                          onChange={(e) => {
                            formik.setFieldValue("copy_assessments", e.target.checked);
                          }}
                          onBlur={formik.handleBlur}
                          disabled={fetchingDetails}
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                            marginTop: 0,
                            marginLeft: 0,
                            marginRight: "10px",
                            accentColor: "#467fcf",
                            flexShrink: 0,
                          }}
                        />
                        <label
                          className="fw-semibold d-flex align-items-center mb-0"
                          htmlFor="copy_assessments"
                          style={{
                            cursor: "pointer",
                            lineHeight: 1.4,
                            fontFamily: "Segoe UI",
                            fontSize: "14px",
                            color: "#1d1d1d",
                          }}
                        >
                          Copy Assessments
                          <AIEInfoIcon text="Duplicates assessments and shifts dates to match new course dates. Grade settings and assignment links are copied." />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>,
      ]}
      bottomButton={[
        <button
          key="save"
          type="button"
          className="btn btn-save btn-success"
          title="Save"
          onClick={() => formik.handleSubmit()}
          disabled={loading || fetchingDetails}
        >
          {loading ? <i className="fas fa-cog fa-spin"></i> : <i className="fal fa-save"></i>}
          {loading ? " Saving..." : " Save"}
        </button>,
        <button
          key="cancel"
          type="button"
          title="Cancel"
          className="btn btn-danger"
          data-dismiss="modal"
          onClick={() => handleClose()}
          disabled={loading}
        >
          <i className="fal fa-times"></i>
          Cancel
        </button>,
      ]}
      otherProps={{ secondPopupCss: "zindexadd" }}
    />
  );
}